/*
 * Copyright 2024 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { forwardRef } from 'react';
import type { ContainerProps } from './types';
import { useDefinition } from '../../hooks/useDefinition';
import { ContainerDefinition } from './definition';

/**
 * A centered layout wrapper that constrains content to a maximum width and provides consistent page-level padding.
 *
 * @public
 */
export const Container = forwardRef<HTMLDivElement, ContainerProps>(
  (props, ref) => {
    const { ownProps, restProps, utilityStyle } = useDefinition(
      ContainerDefinition,
      props,
    );
    const { classes, children, style } = ownProps;

    return (
      <div
        ref={ref}
        className={classes.root}
        style={{ ...utilityStyle, ...style }}
        {...restProps}
      >
        {children}
      </div>
    );
  },
);

Container.displayName = 'Container';
